import * as statusCodes from 'stoker/http-status-codes';
import z from 'zod';
const errorTypes = {
    VALIDATION: 'validation',
    AUTHENTICATION: 'authentication',
    PERMISSION: 'permission',
    NOT_FOUND: 'not_found',
    RATE_LIMIT: 'rate_limit',
    INTERNAL: 'internal',
};
const errorTypesArray = Object.values(errorTypes);
const ErrorResponseSchema = z
    .object({
    type: z.enum(errorTypesArray).meta({
        description: 'The error type category',
        examples: ['validation', 'authentication'],
    }),
    code: z.string().meta({
        description: 'Machine-readable error code identifier',
        examples: ['validation.invalid_parameter', 'authentication.unauthorized'],
    }),
    message: z.string().meta({
        description: 'Human-readable error message',
        examples: ['The request was invalid', 'Authentication required'],
    }),
    param: z
        .string()
        .optional()
        .meta({
        description: 'The parameter that caused the error (if applicable)',
        examples: ['email', 'user_id'],
    }),
    details: z
        .object({
        generalErrors: z.array(z.string()).meta({
            description: 'Validation errors that do not relate to a specific parameter',
            // @todo: add examples
            examples: [],
        }),
        fieldErrors: z.record(z.string(), z.array(z.string())).meta({
            description: 'Errors related to specific parameters',
            examples: [{ email: ['Invalid email address'] }],
        }),
    })
        .optional()
        .meta({
        description: 'Zod validation errors',
        examples: [
            { generalErrors: ['Invalid request'] },
            { fieldErrors: { email: ['Invalid email address'] } },
        ],
    }),
})
    .meta({
    description: 'ErrorResponse',
    examples: [
        {
            type: 'authentication',
            code: 'Authentication.unauthorized',
            message: 'Authentication required',
        },
    ],
});
/**
 * Standardized error codes for the API
 */
const ErrorCodes = {
    // Validation error (400)
    Validation: {
        INVALID_PARAMETER: 'validation.invalid_parameter',
        MISSING_REQUIRED_FIELD: 'validation.missing_required_field',
        INVALID_FORMAT: 'validation.invalid_format',
        ALREADY_EXISTS: 'validation.already_exists',
        IN_USE: 'validation.resource_in_use',
        INVALID_STATE: 'validation.invalid_state',
    },
    // Authentication error (401)
    Authentication: {
        UNAUTHORIZED: 'authentication.unauthorized',
        INVALID_CREDENTIALS: 'authentication.invalid_credentials',
        INVALID_TOKEN: 'authentication.invalid_token',
        EXPIRED_TOKEN: 'authentication.expired_token',
    },
    // Permission errors (403)
    Permission: {
        FORBIDDEN: 'permission.forbidden',
        INSUFFICIENT_PERMISSIONS: 'permission.insufficient_permissions',
        ACCOUNT_RESTRICTED: 'permission.account_restricted',
    },
    // Not found error (404)
    NotFound: {
        RESOURCE_NOT_FOUND: 'not_found.resource_not_found',
    },
    // Rate limit error (429)
    RateLimit: {
        TOO_MANY_REQUESTS: 'rate_limit.too_many_requests',
        QUOTA_EXCEEDED: 'rate_limit.quota_exceeded',
    },
    // Internal server error (500)
    Internal: {
        INTERNAL_ERROR: 'internal.internal_error',
        DATABASE_ERROR: 'internal.database_error',
        SERVICE_UNAVAILABLE: 'internal.service_unavailable',
        TIMEOUT: 'internal.timeout',
        DEPENDENCY_FAILURE: 'internal.dependency_failure',
    },
};
class AppError extends Error {
    type;
    code;
    param;
    details;
    context;
    cause;
    constructor({ type, code, message, param, details, cause, context, }) {
        super(message, { cause });
        this.type = type;
        this.code = code;
        this.param = param;
        this.details = details;
        this.context = context;
        this.cause = cause;
    }
    /**
     * Convert this error to an HTTP status code
     */
    statusCode() {
        switch (this.type) {
            case 'validation':
                return statusCodes.BAD_REQUEST; // 400
            case 'authentication':
                return statusCodes.UNAUTHORIZED; // 401
            case 'permission':
                return statusCodes.FORBIDDEN; // 403
            case 'not_found':
                return statusCodes.NOT_FOUND; // 404
            case 'rate_limit':
                return statusCodes.TOO_MANY_REQUESTS; // 429
            case 'internal':
                return statusCodes.INTERNAL_SERVER_ERROR; // 500
            // Error out if the type is not one of the above
            default: {
                const _exhaustiveCheck = this.type;
                return _exhaustiveCheck;
            }
        }
    }
    /**
     * Convert this error to a standard response object
     */
    toResponse() {
        return {
            type: this.type,
            code: this.code,
            message: this.message,
            ...(this.param && { param: this.param }),
            ...(this.details && { details: this.details }),
        };
    }
}
export { AppError, ErrorCodes, ErrorResponseSchema, };
//# sourceMappingURL=error.js.map